// libsodium-wrappers-sumo is ~1 MB (≈400 KB gzipped) and was previously a
// top-level import, so it landed in the eager bundle of *every* page (it is
// reached from the dashboard/admin layouts via the encryption store). It is
// only needed once a crypto operation actually runs (enroll, unlock, device
// ops), so we load it lazily here: the module-scope `sodium` binding is
// assigned inside initCrypto() via dynamic import, and every exported function
// already gates on `await initCrypto()` before touching `sodium` — so their
// bodies are unchanged. This keeps libsodium out of first paint.
//
// Type-only default import carries the full sodium surface for type-checking
// and is fully erased at build time (no runtime weight) — it matches the
// runtime `mod.default` we assign in initCrypto().
import type SodiumDefault from 'libsodium-wrappers-sumo';

let sodium!: typeof SodiumDefault;

// Single-flight init: the first caller starts the import + sodium.ready; every
// concurrent caller awaits the same promise, so `sodium` is guaranteed assigned
// and ready by the time any `await initCrypto()` resolves. (No `initialized`
// flag — that left a window where two callers could both run the import.)
let initPromise: Promise<void> | null = null;

export function initCrypto(): Promise<void> {
  if (!initPromise) {
    initPromise = (async () => {
      const mod = await import('libsodium-wrappers-sumo');
      sodium = mod.default;
      await sodium.ready;
    })();
  }
  return initPromise;
}

/** Generate X25519 keypair for device enrollment. */
export async function generateDeviceKey(): Promise<{ publicKey: Uint8Array; privateKey: Uint8Array }> {
  await initCrypto();
  const kp = sodium.crypto_box_keypair();
  return { publicKey: kp.publicKey, privateKey: kp.privateKey };
}

/** Generate a random 256-bit key (Master KEK or DEK). */
export async function generateKey(): Promise<Uint8Array> {
  await initCrypto();
  return sodium.randombytes_buf(32);
}

/** Wrap Master KEK to a device's public key using NaCl box. */
export async function wrapMasterKEK(masterKEK: Uint8Array, recipientPubKey: Uint8Array): Promise<Uint8Array> {
  await initCrypto();
  const ephKP = sodium.crypto_box_keypair();
  const nonce = sodium.randombytes_buf(sodium.crypto_box_NONCEBYTES);
  const sealed = sodium.crypto_box_easy(masterKEK, nonce, recipientPubKey, ephKP.privateKey);
  // Format: nonce(24) + ephPub(32) + ciphertext
  const result = new Uint8Array(nonce.length + ephKP.publicKey.length + sealed.length);
  result.set(nonce, 0);
  result.set(ephKP.publicKey, nonce.length);
  result.set(sealed, nonce.length + ephKP.publicKey.length);
  return result;
}

/** Unwrap Master KEK using device private key. */
export async function unwrapMasterKEK(wrapped: Uint8Array, privateKey: Uint8Array): Promise<Uint8Array> {
  await initCrypto();
  const nonceLen = sodium.crypto_box_NONCEBYTES;  // 24
  const pubLen = sodium.crypto_box_PUBLICKEYBYTES; // 32
  const nonce = wrapped.slice(0, nonceLen);
  const senderPub = wrapped.slice(nonceLen, nonceLen + pubLen);
  const ciphertext = wrapped.slice(nonceLen + pubLen);
  return sodium.crypto_box_open_easy(ciphertext, nonce, senderPub, privateKey);
}

/**
 * Wrap DEK with Master KEK using XChaCha20-Poly1305-IETF.
 *
 * Both frontend (libsodium) and backend (golang.org/x/crypto/chacha20poly1305.NewX)
 * use XChaCha20-Poly1305 with 24-byte nonces. Format: nonce(24) + ciphertext.
 */
export async function wrapDEK(dek: Uint8Array, masterKEK: Uint8Array): Promise<Uint8Array> {
  await initCrypto();
  const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
  const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(dek, null, null, nonce, masterKEK);
  const result = new Uint8Array(nonce.length + ciphertext.length);
  result.set(nonce, 0);
  result.set(ciphertext, nonce.length);
  return result;
}

/** Unwrap DEK with Master KEK. */
export async function unwrapDEK(wrapped: Uint8Array, masterKEK: Uint8Array): Promise<Uint8Array> {
  await initCrypto();
  const nonceLen = sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES;
  const nonce = wrapped.slice(0, nonceLen);
  const ciphertext = wrapped.slice(nonceLen);
  return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(null, ciphertext, null, nonce, masterKEK);
}

/** Derive master KEK from recovery password + stored recovery data. */
export async function unwrapWithRecoveryPassword(
  saltB64: string,
  wrappedB64: string,
  password: string,
): Promise<Uint8Array> {
  await initCrypto();
  const salt = Uint8Array.from(atob(saltB64), (c) => c.charCodeAt(0));
  const wrapped = Uint8Array.from(atob(wrappedB64), (c) => c.charCodeAt(0));
  const derivedKey = sodium.crypto_pwhash(
    32,
    password,
    salt,
    sodium.crypto_pwhash_OPSLIMIT_MODERATE,
    sodium.crypto_pwhash_MEMLIMIT_MODERATE,
    sodium.crypto_pwhash_ALG_ARGON2ID13,
  );
  const nonceLen = sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES;
  const nonce = wrapped.slice(0, nonceLen);
  const ciphertext = wrapped.slice(nonceLen);
  return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(null, ciphertext, null, nonce, derivedKey);
}

/** Wrap Master KEK with recovery password via Argon2id + XChaCha20-Poly1305. */
export async function wrapWithRecoveryPassword(
  masterKEK: Uint8Array,
  password: string,
): Promise<{ salt: Uint8Array; wrapped: Uint8Array }> {
  await initCrypto();
  const salt = sodium.randombytes_buf(sodium.crypto_pwhash_SALTBYTES);
  const derivedKey = sodium.crypto_pwhash(
    32,
    password,
    salt,
    sodium.crypto_pwhash_OPSLIMIT_MODERATE,
    sodium.crypto_pwhash_MEMLIMIT_MODERATE,
    sodium.crypto_pwhash_ALG_ARGON2ID13,
  );
  const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
  const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(masterKEK, null, null, nonce, derivedKey);
  const wrapped = new Uint8Array(nonce.length + ciphertext.length);
  wrapped.set(nonce, 0);
  wrapped.set(ciphertext, nonce.length);
  return { salt, wrapped };
}
